import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'
import PropTypes from 'prop-types'

// Helpers
import { formatAbbrCurrency } from '../../../../helpers/formatters'

// Components
import {
  Button,
  Form,
  FormField,
  Ruler,
  Text,
  If,
  Unless,
  BotHeadSpin,
} from '../../..'
import {
  IconCheck,
  IconArrowLeft,
  IconArrowRight,
} from '../../Icon'
import DollarInput from '../../Input/DollarInput'
import { SlideOnMount, FadeOnMount } from '../../Animations'

// Hooks
import { useTuneValue } from '../../../../hooks/useTuneValue'
import withComposedClassName from '../../../../composables/withComposedClassName'

// Actions
import { addHomeValuation } from '../../../../actions/valuation'
import { flashNotification } from '../../../../actions/notification'
import { CLIENT_TUNED_MORTGAGE_COACH_VALUE } from '../../../../actions/actionTypes'

// Constants
import { KEY_CODES } from '../../../../constants/utility'
import { ERRORS } from '../../../../constants/messages'

// Selectors
import {
  selectHomeId,
  selectHomeUuid,
  selectValuationBounds,
} from '../../../../store/selectors/home'
import { selectHomeValue } from '../../../../store/selectors/home/metadata'
import { selectRequestedValuationFailure } from '../../../../store/selectors/valuation'
import { selectClientHasTunedMortgageCoachValue } from '../../../../store/selectors/mortgageCoach'
import { selectLatestValuationIsTuneYourValue } from '../../../../store/selectors/valuationHistory'

// Utils
import { isTouchDevice } from '../../../../helpers/browser'

// Styles
import panelStyles from '../../Panel/Panel.module.scss'
import styles from './MortgageCoachTuneValue.module.scss'

const propTypes = {
  handleConfirm: PropTypes.func.required,
  handleClose: PropTypes.func.required,
}

const DOLLARS = 'dollars'
const Slide = props => <SlideOnMount interval={50} top='-80px' {...props} />
const Fade = props => <FadeOnMount delay={150} {...props} />
const StyledDollarInput = withComposedClassName(styles.input)(DollarInput)

const MortgageCoachTuneValue = ({ handleConfirm, handleClose }) => {
  const dispatch = useDispatch()

  const bounds = useSelector(selectValuationBounds)
  const homeId = useSelector(selectHomeId)
  const homeUuid = useSelector(selectHomeUuid)
  const homeValue = useSelector(selectHomeValue)

  const {
    getValueFromEvent,
    inputFocused,
    inputValue,
    rulerValue,
    setInputFocused,
    setInputValue,
    setRulerValue,
    setTuneValue,
    tuneValue,
    useMax,
    useMin,
    usePins,
    useStep,
  } = useTuneValue({
    price: homeValue,
    useStep: 1000,
    bounds,
  })

  const useValue = parseInt(inputValue || tuneValue, 10)
  const latestValuationIsTyv = useSelector(selectLatestValuationIsTuneYourValue)
  const valuationSavingOrRefreshError = useSelector(
    selectRequestedValuationFailure
  )
  const mortgageCoachTuneValueUnmounted = useSelector(
    selectClientHasTunedMortgageCoachValue
  )
  const [interacted, setInteracted] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (latestValuationIsTyv && !mortgageCoachTuneValueUnmounted) {
      // Here we can assume the valuation update has completed and they haven't left the TYV flow, so we fire TCA request
      handleConfirm()
    } else if (valuationSavingOrRefreshError) {
      dispatch(
        flashNotification({
          message: ERRORS.communicationError,
          messageType: 'error',
        })
      )
      handleClose()
    }
    return function cleanup() {
      dispatch({
        type: CLIENT_TUNED_MORTGAGE_COACH_VALUE,
      })
    }
  }, [
    latestValuationIsTyv,
    valuationSavingOrRefreshError,
    handleConfirm,
    handleClose,
    dispatch,
    mortgageCoachTuneValueUnmounted,
  ])

  const handleSubmit = e => {
    e.preventDefault()
    setLoading(true)
    dispatch(addHomeValuation(homeId, homeUuid, useValue))
  }

  return (
    <>
      <If condition={loading}>
        <BotHeadSpin panelSpacing />
      </If>
      <Unless condition={loading}>
        <Form
          className={classnames(panelStyles.container, styles.container)}
          fields={[{ name: DOLLARS, parse: DollarInput.parseValue }]}
          onSubmit={handleSubmit}
        >
          <Slide className={styles.header}>
            <Text size='xl' variant='neutralCool' variantCount='900'>
              <FormattedMessage
                id='MortgageCoach.tuneValue.header'
                defaultMessage='Can you confirm what you think your house is worth?'
              />
            </Text>
            <div className={styles.subheader}>
              <Text size='s' variant='neutralCool' variantCount='600'>
                <FormattedMessage
                  id='MortgageCoach.tuneValue.subheader'
                  defaultMessage='We’ll use this to build your Total Cost Analysis and tune your Homebot value'
                />
              </Text>
            </div>
          </Slide>

          <Slide className={styles.rulerWrapEscape}>
            <div className={styles.rulerWrap}>
              <div className={styles.valueDisplay}>
                <FormField
                  input={StyledDollarInput}
                  hideLabel
                  value={inputValue || tuneValue}
                  name={DOLLARS}
                  onMouseOver={() => setInteracted(true)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={e => {
                    const value = getValueFromEvent(e)
                    if (value && value !== inputValue) {
                      const rounded = Math.round(value / useStep) * useStep
                      if (rulerValue !== rounded) {
                        // new ruler value: set to null to force scroll behavior
                        setRulerValue(null)
                      }

                      setInputValue(null)
                      setTuneValue(`${value}`) // Bit of a hack, but a string value displays fine while also failing any direct === comparisons ensuring at least one onChange pass when the ruler slides
                    }
                    setInputFocused(false)
                  }}
                  onKeyDown={e => {
                    e.stopPropagation()
                    if ((e.which || e.keyCode) === KEY_CODES.enter) {
                      e.target.blur()
                    }
                  }}
                >
                  <FormattedMessage
                    id='TYV.input.value'
                    defaultMessage='Your home value'
                  />
                </FormField>
                <Button
                  tabIndex='-1'
                  type='button'
                  className={classnames(
                    styles.confirm,
                    inputFocused && styles.showConfirm
                  )}
                  iconStart={IconCheck}
                />
              </div>
              {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
              <div
                className={styles.rulerEscape}
                onMouseOver={() => setInteracted(true)}
              >
                <Ruler
                  className={styles.ruler}
                  value={rulerValue || tuneValue}
                  min={useMin}
                  max={useMax}
                  step={useStep}
                  pins={usePins}
                  minorTick={useStep * 5}
                  majorTick={useStep * 10}
                  intervalWidth={8}
                  labelRenderFunc={formatAbbrCurrency}
                  onChange={e => {
                    const value = getValueFromEvent(e)
                    if (value) {
                      setRulerValue(value)
                      if (inputValue !== null) {
                        // above condition allows masking incremental changes until useEffect hook above finds inputValue is relevant again
                        setInputValue(value)
                        setTuneValue(value)
                      }
                    }
                  }}
                />
              </div>
              <div className={styles.legend}>
                <div className={styles.left}>
                  <Text
                    size='s'
                    variant='neutralCool'
                    variantCount='700'
                    className={styles.uppercase}
                  >
                    <FormattedMessage
                      id='TYV.valuation.ruler.legend.low'
                      defaultMessage='Low range'
                    />
                  </Text>
                  <Text
                    size='l'
                    variant='neutralCool'
                    variantCount='700'
                    weight='semibold'
                  >
                    {formatAbbrCurrency(useMin)}
                  </Text>
                </div>
                <Text
                  variant='neutralCool'
                  variantCount='600'
                  weight='semibold'
                  className={classnames(
                    styles.directions,
                    interacted && styles.hide,
                    isTouchDevice() && styles.skip
                  )}
                >
                  <IconArrowLeft />
                  <FormattedMessage
                    id='TYV.valuation.ruler.info'
                    defaultMessage='Drag left or right to adjust value'
                  />
                  <IconArrowRight />
                </Text>
                <div className={styles.right}>
                  <Text
                    size='s'
                    variant='neutralCool'
                    variantCount='700'
                    className={styles.uppercase}
                  >
                    <FormattedMessage
                      id='TYV.valuation.ruler.legend.high'
                      defaultMessage='High range'
                    />
                  </Text>
                  <Text
                    size='l'
                    variant='neutralCool'
                    variantCount='700'
                    weight='semibold'
                  >
                    {formatAbbrCurrency(useMax)}
                  </Text>
                </div>
              </div>
            </div>
          </Slide>
          <div className={styles.flexFill} />
          <Fade className={classnames(panelStyles.submitWrap, styles.footer)}>
            <Button
              primary
              fullWidth
              disabled={inputFocused}
              onClick={handleSubmit}
            >
              <FormattedMessage
                id='MortgageCoach.tuneValue.footer.btn'
                defaultMessage='Confirm and finish'
              />
            </Button>
          </Fade>
        </Form>
      </Unless>
    </>
  )
}

MortgageCoachTuneValue.propTypes = propTypes
export default MortgageCoachTuneValue
